
import React, { useState, useRef, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getAuth } from 'firebase/auth';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/utils/firebase';

interface NavBarPortfolioSelectorProps {
  selectedPortfolio: string;
  portfolioOptions: Array<{id: string; title: string; role: string | null}>;
  onPortfolioChange: (value: string) => void;
  isMobile?: boolean;
}

// Enhanced role normalization function for consistent comparison and display
const normalizeRoleForDisplay = (role: string | null | undefined): string => {
  if (!role) return '';
  
  // Convert to lowercase and remove spaces for consistent comparison
  const normalizedRole = role.trim().toLowerCase();
  
  // Handle special cases for role display
  if (normalizedRole === 'primaryowner' || 
      normalizedRole === 'primary owner' || 
      normalizedRole === 'primary') {
    return 'Primary Owner';
  } else if (normalizedRole === 'owner') {
    return 'Owner';
  } else if (normalizedRole === 'manager') {
    return 'Manager';
  } else if (normalizedRole === 'member') {
    return 'Member';
  }
  
  // Capitalize first letter for other roles
  return role.trim().charAt(0).toUpperCase() + role.trim().slice(1);
};

export const NavBarPortfolioSelector: React.FC<NavBarPortfolioSelectorProps> = ({
  selectedPortfolio,
  portfolioOptions,
  onPortfolioChange,
  isMobile = false
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const lastSelectedValueRef = useRef<string>(selectedPortfolio);
  const processingTimeoutRef = useRef<number | null>(null);
  const changeTimeoutRef = useRef<number | null>(null);
  const [userRoles, setUserRoles] = useState<Record<string, string>>({});
  const auth = getAuth();
  const userId = auth.currentUser?.uid;
  const userEmail = auth.currentUser?.email;

  // Update lastSelectedValueRef when selectedPortfolio changes externally
  useEffect(() => {
    if (selectedPortfolio && selectedPortfolio !== lastSelectedValueRef.current) {
      console.log(`Updating lastSelectedValueRef from ${lastSelectedValueRef.current} to ${selectedPortfolio}`);
      lastSelectedValueRef.current = selectedPortfolio;
    }
  }, [selectedPortfolio]);

  // Improved Firestore query to fetch user roles
  useEffect(() => {
    const fetchUserRoles = async () => {
      if ((!userId && !userEmail) || portfolioOptions.length === 0) return;
      
      console.log(`NavBarPortfolioSelector - Fetching roles for user ID: ${userId}, email: ${userEmail}`);
      const roles: Record<string, string> = {};
      
      // For each portfolio, check if user is a member and get role
      for (const portfolio of portfolioOptions) {
        try {
          const portfolioId = portfolio.id;
          // Direct query to the portfolios collection
          const portfolioRef = doc(db, 'portfolios', portfolioId);
          const portfolioSnap = await getDoc(portfolioRef);
          
          if (portfolioSnap.exists()) {
            const portfolioData = portfolioSnap.data();
            const members = portfolioData.members || [];
            
            // Enhanced member finding logic - check both userId and email
            const userMember = members.find((m: any) => {
              const memberUserId = m.userId;
              const memberEmail = m.email;
              
              // Match on userId or email
              return (userId && memberUserId === userId) || 
                     (userEmail && memberEmail === userEmail);
            });
            
            if (userMember) {
              const rawRole = userMember.role || '';
              const normalizedRole = normalizeRoleForDisplay(rawRole);
              roles[portfolioId] = normalizedRole;
              
              console.log(`Found user in portfolio ${portfolioId} with raw role "${rawRole}", normalized to "${normalizedRole}"`);
            } else {
              console.log(`User not found in members of portfolio ${portfolioId}`);
            }
          } else {
            console.log(`Portfolio document ${portfolioId} does not exist`);
          }
        } catch (error) {
          console.error(`Error fetching role for portfolio ${portfolio.id}:`, error);
        }
      }
      
      setUserRoles(roles);
      console.log("User portfolio roles:", roles);
    };
    
    fetchUserRoles();
  }, [userId, userEmail, portfolioOptions]);

  // Clear timeouts on unmount
  useEffect(() => {
    return () => {
      if (processingTimeoutRef.current) {
        window.clearTimeout(processingTimeoutRef.current);
      }
      if (changeTimeoutRef.current) {
        window.clearTimeout(changeTimeoutRef.current);
      }
    };
  }, []);

  // Handle portfolio selection with proper safeguards
  const handlePortfolioSelection = (value: string) => {
    // Skip if we're already processing a selection
    if (isProcessing) {
      console.log(`Skipping portfolio selection while processing: ${value}`);
      return;
    }
    
    console.log(`Portfolio selection in NavBar: ${value}, isMobile: ${isMobile}, previous: ${lastSelectedValueRef.current}`);
    
    // Skip if selecting the same portfolio again
    if (value === lastSelectedValueRef.current) {
      console.log(`Skipping duplicate portfolio selection: ${value}`);
      return;
    }
    
    // Set processing flag to prevent multiple triggers
    setIsProcessing(true);
    
    // Clear any pending timeouts
    if (changeTimeoutRef.current) {
      window.clearTimeout(changeTimeoutRef.current);
    }
    
    // Update the last selected value reference
    lastSelectedValueRef.current = value;
    
    // Call the change handler immediately
    console.log(`Executing portfolio change: ${value}`);
    onPortfolioChange(value);
    
    // Reset the processing state after a delay
    processingTimeoutRef.current = window.setTimeout(() => {
      setIsProcessing(false);
      processingTimeoutRef.current = null;
    }, isMobile ? 600 : 300);
  };

  // If no portfolio options are available, show a disabled placeholder
  if (!portfolioOptions || portfolioOptions.length === 0) {
    return (
      <Select disabled>
        <SelectTrigger className={`${isMobile ? "w-full" : "w-64"} bg-white shadow-sm border-gray-200`}>
          <SelectValue>No portfolios available</SelectValue>
        </SelectTrigger>
      </Select>
    );
  }

  // Find the selected portfolio and get its display information
  const selectedPortfolioInfo = portfolioOptions.find(p => p.id === selectedPortfolio);
  const userRole = selectedPortfolio ? (userRoles[selectedPortfolio] || '') : '';

  return (
    <Select 
      value={selectedPortfolio} 
      onValueChange={handlePortfolioSelection}
      defaultValue={selectedPortfolio}
      disabled={isProcessing}
    >
      <SelectTrigger className={`${isMobile ? "w-full" : "w-64"} bg-white shadow-sm border-gray-200`}>
        <SelectValue>
          <div className="flex items-center">
            <span>{selectedPortfolioInfo?.title || 'Select Portfolio'}</span>
            {userRole && (
              <span className="ml-2 px-2 py-0.5 bg-gray-100 text-xs rounded-full">
                {userRole}
              </span>
            )}
          </div>
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {portfolioOptions.map((p) => (
          <SelectItem key={p.id} value={p.id}>
            <div className="flex items-center justify-between w-full">
              <span>{p.title}</span>
              {userRoles[p.id] && (
                <span className="ml-2 px-2 py-0.5 bg-gray-100 text-xs rounded-full">
                  {userRoles[p.id]}
                </span>
              )}
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
